import React from "react";
import { Link } from "react-router-dom";
import { Section, Eyebrow } from "@/components/domain/Bits";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ListChecks, MessageCircle, Mail, Phone, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STEPS = [
  "Elige tu sorteo", "Selecciona tus boletos", "Captura tus datos", "Crea tu reserva",
  "Realiza la transferencia", "Sube tu comprobante", "Espera la validación", "Consulta tus boletos",
  "Sigue el sorteo en vivo", "Consulta el resultado",
];

export function HowToParticipate() {
  return (
    <Section className="py-14">
      <Eyebrow><ListChecks className="h-3.5 w-3.5" />Guía</Eyebrow>
      <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-900">Cómo participar</h1>
      <p className="mt-2 max-w-xl text-slate-600">Diez pasos claros, sin presión y con total transparencia.</p>
      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand font-mono text-sm font-bold text-white">{i + 1}</span>
            <span className="font-medium text-slate-800">{s}</span>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center">
        <Button asChild size="lg" className="rounded-full bg-brand hover:bg-brand-hover"><Link to="/sorteos">Empezar ahora</Link></Button>
      </div>
    </Section>
  );
}

const FAQ_GROUPS = {
  "Participación": [["¿Necesito una cuenta para participar?", "No. Puedes participar y consultar tu folio sin registrarte."], ["¿Puedo elegir mis números?", "Sí, de forma manual, aleatoria o con la Maquinita de la Suerte."]],
  "Pagos": [["¿Qué métodos de pago aceptan?", "Transferencia bancaria a cuentas oficiales verificadas."], ["¿Subir comprobante confirma mi pago?", "No. Un comprobante enviado será validado por nuestro equipo antes de confirmar tus boletos."]],
  "Comprobantes": [["¿Qué archivos puedo subir?", "JPG, PNG o PDF de hasta 10 MB. Se almacenan de forma privada."]],
  "Ganadores": [["¿Cómo verifico un ganador?", "Cada ganador tiene un perfil público con número ganador y evidencia de entrega."]],
  "Seguridad": [["¿Cómo evito fraudes?", "Verifica siempre las cuentas y canales oficiales en el Centro de Transparencia. Ningún vendedor usa cuentas personales."]],
};

export function FAQ() {
  return (
    <Section className="py-14">
      <Eyebrow>Ayuda</Eyebrow>
      <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-900">Preguntas frecuentes</h1>
      <div className="mt-8 space-y-8">
        {Object.entries(FAQ_GROUPS).map(([group, items]) => (
          <div key={group}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">{group}</h2>
            <Accordion type="single" collapsible className="rounded-xl border border-slate-200 bg-white px-4">
              {items.map(([q, a], i) => (
                <AccordionItem key={i} value={`${group}-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-semibold">{q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Contact() {
  const submit = (e) => { e.preventDefault(); toast.success("Mensaje enviado. Te responderemos por canales oficiales."); };
  return (
    <Section className="py-14">
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <Eyebrow>Contacto</Eyebrow>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-900">Hablemos</h1>
          <p className="mt-2 text-slate-600">Atención únicamente por canales oficiales verificados.</p>
          <div className="mt-8 space-y-3">
            {[[MessageCircle, "WhatsApp oficial", "+52 33 1234 5678"], [Mail, "Correo", "hola@circulodorado.mx"], [Phone, "Teléfono", "+52 33 8000 1000"]].map(([Icon, t, v], i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-brand"><Icon className="h-5 w-5" /></div>
                <div><div className="text-xs text-slate-400">{t}</div><div className="font-semibold text-slate-900">{v}</div></div>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-4">
            <Input placeholder="Tu nombre" required data-testid="contact-name" />
            <Input type="email" placeholder="Tu correo" required data-testid="contact-email" />
            <Textarea placeholder="¿En qué te ayudamos?" rows={5} required data-testid="contact-message" />
            <Button type="submit" className="w-full rounded-full bg-brand hover:bg-brand-hover" data-testid="contact-submit"><Send className="mr-2 h-4 w-4" />Enviar mensaje</Button>
          </div>
        </form>
      </div>
    </Section>
  );
}
